// client/src/components/auth/ProtectedRoute.tsx (TypeScript since ST-00303; was ProtectedRoute.js)
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getRequiredPermission,
  permits,
  type RequiredPermission,
} from '../../config/pagePermissions';
import LexiBridgeLoaderJs from '../common/LexiBridgeLoader';
import ConnectionProblem from './ConnectionProblem';
import './Login.css';

// LexiBridgeLoader.js is still JavaScript: its props as it documents them.
const LexiBridgeLoader = LexiBridgeLoaderJs as unknown as (props: {
  message?: string;
  compact?: boolean;
}) => ReactNode;

export interface ProtectedRouteProps {
  children?: ReactNode;
  /** Any one of these app roles is enough. */
  roles?: readonly string[];
  /** Overrides the path's entry in config/pagePermissions. */
  requiredPermission?: RequiredPermission;
}

/**
 * Guards a route. In order:
 *   1. wait for auth to initialize — and, once authenticated, for the tenant +
 *      permission context to finish loading (ST-00147)
 *   2. when auth is disabled (dev), allow
 *   3. require an authenticated session — but when the session check could not
 *      reach LexiBridge, say so instead of sending the user to sign in (ST-00303)
 *   4. enforce role(s) if a `roles` prop is given
 *   5. enforce the page permission — taken from the `requiredPermission` prop,
 *      else derived from the current path via the central pagePermissions map.
 *      Pages with no mapped permission are open to any authenticated user.
 */
const ProtectedRoute = ({ children, roles = [], requiredPermission }: ProtectedRouteProps) => {
  const {
    authenticated,
    loading,
    authEnabled,
    contextReady,
    connectionProblem,
    hasAnyRole,
    hasPermission,
  } = useAuth();
  const location = useLocation();

  // Show branded loader on dark background matching auth screens. Also hold here
  // while authenticated but the permission list has not arrived yet: evaluating
  // hasPermission() against [] would bounce a legitimate user to /unauthorized.
  if (loading || (authEnabled && authenticated && !contextReady)) {
    return (
      <div className="auth-loading-page">
        <div className="login-bg-grid" />
        <div className="login-bg-glow login-bg-glow--teal" />
        <div className="login-bg-glow login-bg-glow--purple" />
        <LexiBridgeLoader message="Verifying session" />
      </div>
    );
  }

  // If authentication is disabled, allow access
  if (!authEnabled) {
    return <>{children}</>;
  }

  // Not signed in: to the sign-in screen — unless LexiBridge could not be reached
  // to find out, when the session may be fine (keep the page's address).
  if (!authenticated) {
    return connectionProblem ? <ConnectionProblem /> : <Navigate to="/login" replace />;
  }

  // If roles are specified, check if user has required role
  if (roles.length > 0 && !hasAnyRole(roles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Enforce the page permission (explicit prop wins; otherwise derive from path).
  const permission = requiredPermission || getRequiredPermission(location.pathname);
  if (!permits(permission, hasPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRoute;
